import apiClient from '../apiClient';
import type { ApiResponse, Session, PaginationMeta } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';

const BASE_URL = '/dashboard/sessions';

interface PaginatedResponse {
  items: Session[];
  pagination: PaginationMeta;
}

export const getSessions = async (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  machineId?: string,
  status?: string,
  type?: string,
  dateFrom?: string,
  dateTo?: string
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(BASE_URL, {
    page,
    limit,
    search,
    sortBy,
    sortOrder,
    machineId,
    status,
    type,
    dateFrom,
    dateTo,
  });
  const { data } = await apiClient.get<ApiResponse<Session[]>>(url);
  return {
    items: data.data,
    pagination: data.meta!,
  };
};

// Funds command: send MQTT funds command to machine for an active session
export type FundsCommandType = 'add' | 'debit' | 'commit' | 'rollback';

export const sendSessionFundsCommand = async (
  sessionId: string,
  body: { type: FundsCommandType; amount?: number; reason?: string }
): Promise<{ sessionId: string; deviceId: string; type: string; amount?: number; reason?: string }> => {
  const { data } = await apiClient.post(`/admin/sessions/${sessionId}/funds-command`, body);
  return data.data;
};

export const useSendSessionFundsCommand = () =>
  useMutation({
    mutationFn: ({ sessionId, ...body }: { sessionId: string; type: FundsCommandType; amount?: number; reason?: string }) =>
      sendSessionFundsCommand(sessionId, body),
  });

// Seed a machine with admin-granted credit (creates synthetic session)
export const seedMachineFunds = async (
  machineId: string,
  body: { amount: number; reason?: string; timeoutMinutes?: number }
): Promise<{ sessionId: string; machineId: string; deviceId: string; amount: number; expiresAt: string }> => {
  const { data } = await apiClient.post(`/admin/machines/${machineId}/seed-funds`, body);
  return data.data;
};

export const useSeedMachineFunds = () =>
  useMutation({
    mutationFn: ({ machineId, ...body }: { machineId: string; amount: number; reason?: string; timeoutMinutes?: number }) =>
      seedMachineFunds(machineId, body),
  });

// React Query Hooks

export const useGetSessions = (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  machineId?: string,
  status?: string,
  type?: string,
  dateFrom?: string,
  dateTo?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_SESSIONS, page, limit, search, sortBy, sortOrder, machineId, status, type, dateFrom, dateTo],
    queryFn: () => getSessions(page, limit, search, sortBy, sortOrder, machineId, status, type, dateFrom, dateTo),
  });
